<template>
<q-page class="main">
  <q-card class="page-card">
    <q-card-section class="page-card__main-content">
      <div class="row items-end justify-between">
        <slot name="header">
          <h1 class="heading-2 q-mb-lg">Beschikbare projecten</h1>
        </slot>
      </div>
      <BaseMap class="page-card__map"
               :geometry="visiblePolygon"
               auto-fit-always
               canZoom
               canPan />
      <q-list bordered>
        <q-item>
          <common-input
              v-model="searchTerm"
              class="form__input full-width"
              label="Zoek op titel of extern id"
              bg-color="neutral-5"
              dense
              ref="searchInput"
          >
            <template v-slot:prepend>
              <q-icon name="search" aria-hidden="true" />
            </template>
          </common-input>
        </q-item>
        <template v-for="(project, index) in visibleProjects"
                  :key="project.external_uid">
          <q-expansion-item
              group="importableProjects"
              header-class="text-primary"
              @show="switchToProject(project)"
          >
            <template #header>
              <div class="full-width">{{ project.latest_version.title || '-' }}
                <q-chip class="float-right" v-if="project.id">Al geimporteerd</q-chip>
              </div>
            </template>
            <q-card>
              <common-form :action="$route('project-import.store', source.id, page)" ref="importForm">
                <input type="hidden" name="external_uid" :value="project.external_uid" />
                <q-card-section>
                  <table>
                    <tr>
                      <td class="text-bold">Extern id</td>
                      <td>{{ project.external_uid }}</td>
                    </tr>
                    <tr v-if="project.reference_number">
                      <td class="text-bold">Nummer</td>
                      <td>{{ project.reference_number }}</td>
                    </tr>
                    <tr v-if="project.latest_version.client">
                      <td class="text-bold">Opdrachtgever(s)</td>
                      <td>{{ project.latest_version.client }}</td>
                    </tr>
                    <tr v-if="project.latest_version.completion_date">
                      <td class="text-bold">Opleverdatum</td>
                      <td>{{ project.latest_version.completion_date }}</td>
                    </tr>
                    <tr v-if="project.latest_version.manager">
                      <td class="text-bold">Projectleider</td>
                      <td>{{ project.latest_version.manager }}</td>
                    </tr>
                    <tr v-if="project.latest_version.planner">
                      <td class="text-bold">Werkvoorbereider</td>
                      <td>{{ project.latest_version.planner }}</td>
                    </tr>
                    <tr v-if="project.latest_version.designer">
                      <td class="text-bold">Ontwerper</td>
                      <td>{{ project.latest_version.designer }}</td>
                    </tr>
                    <tr v-if="project.latest_version.description">
                      <td class="q-mt-md" colspan="2" v-html="project.latest_version.description"></td>
                    </tr>
                  </table>
                </q-card-section>
                <q-card-section v-if="!project.id">
                  <div class="row q-col-gutter-sm">
                    <div class="col-md-9">
                      <q-select v-model="selectedCategories"
                                name="categories[]"
                                :options="categoryOptions"
                                label="Categorieën"
                                class="text-neutral-90"
                                multiple
                                use-chips
                                outlined
                                map-options
                                emit-value
                                :error-message="$errorMessage('categories')"
                                :error="$hasError('categories')">
                        <template v-slot:selected-item="scope">
                          <q-chip
                              dense
                              removable
                              @remove="scope.removeAtIndex(scope.index)"
                              :tabindex="scope.tabindex"
                              class="common-chip"
                          >
                            {{ scope.opt.label }}
                          </q-chip>
                        </template>
                      </q-select>
                    </div>
                    <div class="col-md-3 text-center">
                      <q-btn rounded @click="selectAllCategories">Selecteer alle Categorie&euml;n</q-btn>
                    </div>
                  </div>
                </q-card-section>
                <q-card-actions align="right">
                  <FlatButton v-if="project.id" :href="$route('project.show', project.id)">
                    Naar project
                  </FlatButton>
                  <AccentButton v-else @click="doImport(index)">Importeer als project</AccentButton>
                </q-card-actions>
              </common-form>
            </q-card>
          </q-expansion-item>
          <q-separator />
        </template>
      </q-list>
    </q-card-section>
    <q-separator />
    <q-card-actions>
      <q-pagination
          v-if="source.is_paginated"
          :modelValue="currentPage"
          :max="projects.length ?  currentPage+ 1 : currentPage"
          direction-links
          unelevated
          color="neutral-90"
          active-color="primary"
          @update:modelValue="navigateToPage"
          boundary-numbers
      />
    </q-card-actions>
  </q-card>
</q-page>
</template>

<script>
import AccentButton from '../../../components/button/AccentButton.vue'
import FlatButton from '../../../components/button/FlatButton.vue'

export default {
  components: { FlatButton, AccentButton },
  props: {
    projects: {
      type: Array,
      default: () => [],
    },
    categories: {
      type: Array,
      default: () => [],
    },
    sources: {
      type: Array,
      default: () => [],
    },
    source: {
      type: Object,
      default: () => ({}),
    },
    page: {
      type: Number,
    },
  },
  data () {
    return {
      currentPage: parseInt(this.page) + 1,
      visiblePolygon: null,
      searchTerm: '',
      selectedCategories: [],
    }
  },
  computed: {
    categoryOptions () {
      return this.categories.map(category => ({
        value: category.id,
        label: category.title,
      }))
    },
    visibleProjects () {
      if (!this.searchTerm) {
        return this.projects
      }
      const term = this.searchTerm.toLowerCase()
      return this.projects.filter(project => {
        if(!project.latest_version.title) {
          return project.external_uid.toLowerCase().includes(this.searchTerm)
        }
        return project.latest_version.title.toLowerCase().includes(this.searchTerm) ||
            project.external_uid.toLowerCase().includes(this.searchTerm)
      })
    },
  },
  methods: {
    navigateToPage (page) {
      return this.$goto('project-import.show', { source: this.source.id, page: page - 1 })
    },
    switchToProject (project) {
      this.visiblePolygon = project.json_polygon
      this.selectedCategories = []
    },
    selectAllCategories () {
      this.selectedCategories = this.categories.map(({ id }) => id)
    },
    async doImport (index) {
      if (!this.$refs.importForm[index]) {
        return
      }
      if (this.selectedCategories.length || await this.$confirm({
        title: 'Geen categorieen geselecteerd',
        messages: ['Weet u zeker dat u dit project wilt importeren met alleen de integrale categorie?'],
      })) {
        this.$refs.importForm[index].submit()
      }
    },
  },
}
</script>
